import { type useTranslations } from "next-intl";
import * as z from "zod";

import { speakerFieldSchema } from "./speaker-fields";

export const MAX_FILES = 32;
// Per-file limits for the authenticated upload flow (UploadVideo).
// Landing and align flows declare their own stricter limits locally.
export const MAX_SIZE_MB = 2000;
export const MAX_DURATION_S = 60 * 60 * 5; // 5 hours

export type UploadFormType = ReturnType<typeof getUploadFormSchema>;
export type UploadFormData = z.infer<ReturnType<typeof getUploadFormSchema>>;

export const getUploadFormSchema = ({ t }: { t: ReturnType<typeof useTranslations> }) => {
  return (
    z
      .object({
        mediaInput: z
          .object({
            // z.custom<File>() accepts anything at runtime (no validator) and
            // narrows the inferred type to File[] in TypeScript. Keeping the
            // schema permissive matches the previous z.unknown() shape — the
            // dropzone handles File-ness; per-file size/duration is enforced
            // by the validation hook.
            files: z.array(z.custom<File>()).max(MAX_FILES),
            youtubeUrl: z.string().optional(),
          })
          .superRefine((data, ctx) => {
            const hasFiles = (data.files?.length ?? 0) > 0;
            const hasUrl = !!data.youtubeUrl;
            if (!hasFiles && !hasUrl) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("uploadForm.noInputErrorMessage"),
                path: [],
              });
            }
            if (hasFiles && hasUrl) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("uploadForm.chooseEitherYoutubeOrFileMessage"),
                path: ["youtubeUrl"],
              });
            }
          }),
        model: z.enum(["tiny", "small", "base", "medium", "large-v2", "large-v3"]),
        transcriptionType: z.enum([
          "MODAL_ASR_AND_ALIGN",
          "MODAL_SW",
          "MODAL_ALIGN",
          "FALAI_WIZPER",
          "ELEVENLABS_SCRIBE",
        ]),
        targetLanguage: z.string().optional(),
        speakers: z.array(speakerFieldSchema),
        text: z.string().optional(),
        isSong: z.boolean(),
      })
      // targetLanguage is required if FALAI_WIZPER
      .refine(
        (data) => {
          if (data.transcriptionType === "FALAI_WIZPER") {
            return !!data.targetLanguage;
          }
          return true;
        },
        {
          message: t("uploadForm.targetLanguageRequiredMessage"),
          path: ["targetLanguage"],
        }
      )
  );
};
